import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

data class TournamentSlotDay(val date: String, val description: String)

fun TournamentSlotDto.day() = TournamentSlotDay(startsAt.take(10), dayLabel)

fun groupedTournamentSlots(slots: List<TournamentSlotDto>): List<Pair<TournamentSlotDay, List<TournamentSlotDto>>> =
    slots.groupBy { it.day() }.toList()
        .sortedWith(compareBy<Pair<TournamentSlotDay, List<TournamentSlotDto>>> { it.first.date }
            .thenBy { it.second.minOf { slot -> slot.startsAt } }.thenBy { it.first.description })
        .map { (day, members) -> day to members.sortedWith(compareBy<TournamentSlotDto> { it.startsAt }.thenBy { it.sortOrder }.thenBy { it.id }) }

fun slotTimeLabel(slot: TournamentSlotDto): String {
    if (slot.endsAt != null && slot.endsAt.take(10) != slot.startsAt.take(10)) {
        return formatSlotDateTimeRange(slot.startsAt, slot.endsAt)
    }
    val start = slot.startsAt.substringAfter('T').take(5)
    return slot.endsAt?.let { "$start–${it.substringAfter('T').take(5)}" } ?: start
}

fun tournamentPlayerSearchText(value: String): String =
    (value.lowercase().asDynamic().normalize("NFD") as String).replace(Regex("[\\u0300-\\u036f]"), "")

@Composable
fun TournamentSlotsTab(
    token: String?, tournament: TournamentDto, data: TournamentAdminData,
    canEdit: Boolean, onReload: () -> Unit,
) {
    var draftDays by remember(tournament.id) { mutableStateOf<List<TournamentSlotDay>>(emptyList()) }
    var addingDay by remember(tournament.id) { mutableStateOf(false) }
    var addingSlotFor by remember(tournament.id) { mutableStateOf<TournamentSlotDay?>(null) }
    var openSlotId by remember(tournament.id) { mutableStateOf<String?>(null) }
    var dirtySlotId by remember(tournament.id) { mutableStateOf<String?>(null) }
    val entriesBySlot = data.entries.groupBy { it.slotId }
    val grouped = groupedTournamentSlots(data.slots).toMap()
    val days = (grouped.keys + draftDays).distinct().sortedBy { it.date }

    fun openSlot(id: String?) {
        if (id == openSlotId) return
        if (dirtySlotId != null && !window.confirm("Zahodit neuložené změny obsazení slotu?")) return
        dirtySlotId = null
        openSlotId = id
    }

    Div {
        H3 { Text("Obsazovačka") }
        if (days.isEmpty()) P { Text("Zatím nejsou připravené žádné dny.") }
        days.forEach { day ->
            key(day) {
                val slots = grouped[day].orEmpty()
                Div(attrs = { classes("tournament-day") }) {
                    Div(attrs = { classes("slot-detail-heading") }) {
                        Div {
                            H4 { Text(formatDate(day.date)) }
                            if (day.description.isNotBlank()) P { Text(day.description) }
                        }
                        if (canEdit) Button(attrs = { onClick { addingSlotFor = day } }) { Text("Přidat slot") }
                    }
                    if (slots.isEmpty()) {
                        P(attrs = { classes("muted") }) { Text("Den se uloží s prvním slotem.") }
                        if (canEdit) Button(attrs = {
                            onClick { draftDays = draftDays - day; if (addingSlotFor == day) addingSlotFor = null }
                        }) { Text("Odebrat rozpracovaný den") }
                    } else {
                        Div(attrs = { classes("table-scroll") }) {
                            Table(attrs = { classes("public-table", "slot-overview-table") }) {
                                Tr {
                                    Th { Text("Čas") }; Th { Text("Popis slotu") }
                                    Th { Text("Obsazenost") }; Th { Text("Detail") }
                                }
                                slots.forEach { slot ->
                                    val count = entriesBySlot[slot.id].orEmpty().size
                                    Tr(attrs = {
                                        classes("slot-overview-row")
                                        if (openSlotId == slot.id) classes("slot-overview-selected")
                                    }) {
                                        Td { Text(slotTimeLabel(slot)) }
                                        Td { Text(slot.label?.takeIf { it.isNotBlank() } ?: "—") }
                                        Td { Span(attrs = {
                                            classes("slot-occupancy"); if (count > slot.capacity) classes("over")
                                        }) { Text("$count / ${slot.capacity}") } }
                                        Td { Button(attrs = {
                                            onClick { openSlot(if (openSlotId == slot.id) null else slot.id) }
                                        }) { Text(if (openSlotId == slot.id) "Zavřít" else "Otevřít") } }
                                    }
                                }
                            }
                        }
                    }
                    if (canEdit && addingSlotFor == day) {
                        TournamentSlotForm(
                            token, tournament.id, day,
                            sortOrder = (data.slots.maxOfOrNull { it.sortOrder } ?: 0) + 1,
                            onCancel = { addingSlotFor = null },
                            onSaved = { addingSlotFor = null; draftDays = draftDays - day; onReload() },
                        )
                    }
                    slots.firstOrNull { it.id == openSlotId }?.let { slot ->
                        key(slot.id) {
                            TournamentSlotCard(
                                token, slot, data, canEdit,
                                onClose = { openSlot(null) },
                                onDirtyChange = { dirty -> dirtySlotId = if (dirty) slot.id else null },
                                onReload = onReload,
                            )
                        }
                    }
                }
            }
        }
        if (canEdit) {
            if (addingDay) {
                TournamentDayForm(
                    onCancel = { addingDay = false },
                    onAdded = { day ->
                        if (day !in days) draftDays = draftDays + day
                        addingDay = false
                        addingSlotFor = day
                    },
                )
            } else Div(attrs = { classes("actions") }) {
                Button(attrs = { onClick { addingDay = true } }) { Text("Přidat den") }
            }
        }
    }
}

@Composable
private fun TournamentDayForm(onCancel: () -> Unit, onAdded: (TournamentSlotDay) -> Unit) {
    var date by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    Div(attrs = { classes("tournament-slot", "tournament-slot-new") }) {
        H4 { Text("Nový den") }
        Label { Text("Datum dne: ") }
        Input(type = InputType.Date) { value(date); attr("aria-label", "Datum dne"); onInput { date = it.value } }
        Div {
            Label { Text("Popis dne: ") }
            Input(type = InputType.Text) {
                value(description); attr("aria-label", "Popis dne")
                placeholder("např. Sobotní kvalifikace"); onInput { description = it.value }
            }
        }
        P(attrs = { classes("muted") }) { Text("Den se trvale uloží po založení prvního slotu.") }
        Div(attrs = { classes("actions") }) {
            Button(attrs = { if (date.isBlank()) disabled(); onClick { onAdded(TournamentSlotDay(date, description.trim())) } }) { Text("Připravit den") }
            Button(attrs = { onClick { onCancel() } }) { Text("Zrušit") }
        }
    }
}

@Composable
private fun TournamentSlotForm(
    token: String?, tournamentId: String, day: TournamentSlotDay,
    sortOrder: Int, slot: TournamentSlotDto? = null, onCancel: () -> Unit, onSaved: () -> Unit,
) {
    var start by remember { mutableStateOf(slot?.startsAt?.substringAfter('T')?.take(5) ?: "") }
    var end by remember { mutableStateOf(slot?.endsAt?.substringAfter('T')?.take(5) ?: "") }
    var label by remember { mutableStateOf(slot?.label ?: "") }
    var capacity by remember { mutableStateOf(slot?.capacity ?: 8) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    Div(attrs = { classes("tournament-slot", "tournament-slot-new") }) {
        H4 { Text(if (slot == null) "Nový slot — ${formatDate(day.date)}" else "Úprava slotu") }
        Div(attrs = { classes("slot-form-fields") }) {
            Label { Text("Čas od: ") }
            Input(type = InputType.Time) {
                value(start); attr("aria-label", "Čas od"); if (saving) disabled(); onInput { start = it.value }
            }
            Label { Text("Do (volitelné): ") }
            Input(type = InputType.Time) {
                value(end); attr("aria-label", "Čas do"); if (saving) disabled(); onInput { end = it.value }
            }
        }
        Div {
            Label { Text("Popis slotu: ") }
            Input(type = InputType.Text) {
                value(label); attr("aria-label", "Popis slotu"); if (saving) disabled()
                placeholder("např. První runda, dráhy 1–4"); onInput { label = it.value }
            }
        }
        NumberField("Kapacita", capacity, !saving) { capacity = it }
        error?.let { P(attrs = { classes("editor-error") }) { Text(it) } }
        Div(attrs = { classes("actions") }) {
            Button(attrs = {
                if (saving || start.isBlank() || capacity < 1 || (end.isNotBlank() && end < start)) disabled()
                onClick {
                    saving = true
                    scope.launch {
                        try {
                            val input = TournamentSlotInput(
                                tournamentId = tournamentId,
                                startsAt = localInputToIso("${day.date}T$start"),
                                endsAt = end.takeIf { it.isNotBlank() }?.let { localInputToIso("${day.date}T$it") },
                                label = label.trim().ifBlank { null }, capacity = capacity,
                                sortOrder = sortOrder, dayLabel = day.description,
                            )
                            if (slot == null) Api.createTournamentSlot(token, input) else Api.updateTournamentSlot(token, slot.id, input)
                            onSaved()
                        } catch (e: Throwable) { error = "Uložení slotu selhalo: ${e.apiMessage()}" }
                        saving = false
                    }
                }
            }) { Text(if (saving) "Ukládám…" else if (slot == null) "Založit slot" else "Uložit slot") }
            Button(attrs = { if (saving) disabled(); onClick { onCancel() } }) { Text("Zrušit") }
        }
    }
}

@Composable
private fun TournamentSlotCard(
    token: String?, slot: TournamentSlotDto, data: TournamentAdminData, canEdit: Boolean,
    onClose: () -> Unit, onDirtyChange: (Boolean) -> Unit, onReload: () -> Unit,
) {
    val assigned = data.entries.filter { it.slotId == slot.id }
    var baseline by remember(slot.id) { mutableStateOf(assigned) }
    var selected by remember(slot.id) { mutableStateOf(assigned.map { it.playerId }.toSet()) }
    var search by remember(slot.id) { mutableStateOf("") }
    var editing by remember(slot.id) { mutableStateOf(false) }
    var busy by remember(slot.id) { mutableStateOf(false) }
    var error by remember(slot.id) { mutableStateOf<String?>(null) }
    var saved by remember(slot.id) { mutableStateOf(false) }
    var guests by remember(slot.id) { mutableStateOf<List<TournamentSlotGuest>>(emptyList()) }
    var moves by remember(slot.id) { mutableStateOf<Map<String, String>>(emptyMap()) }
    var moveToConfirm by remember(slot.id) { mutableStateOf<TournamentSlotMove?>(null) }
    var guestName by remember(slot.id, search) { mutableStateOf(search.trim()) }
    var guestGender by remember(slot.id) { mutableStateOf("male") }
    var guestGroup by remember(slot.id) { mutableStateOf("B") }
    val scope = rememberCoroutineScope()
    val baselineIds = baseline.map { it.playerId }.toSet()
    val dirty = selected != baselineIds
    LaunchedEffect(dirty) { onDirtyChange(dirty) }
    LaunchedEffect(assigned) {
        if (selected == baseline.map { it.playerId }.toSet()) {
            baseline = assigned
            selected = assigned.map { it.playerId }.toSet()
        }
    }
    val localPlayers = guests.map { PlayerDto(it.id, it.name, fullName = it.name, gender = it.gender) }
    val allPlayers = data.allPlayers + localPlayers
    val playerById = (allPlayers + baseline.mapNotNull { it.player }).associateBy { it.id }
    val entryByPlayer = data.entries.associateBy { it.playerId }
    val slotById = data.slots.associateBy { it.id }
    val words = tournamentPlayerSearchText(search).split(Regex("\\s+")).filter { it.isNotBlank() }
    val matchingPlayers = if (words.isEmpty()) emptyList() else allPlayers.filter { p ->
        words.all { word ->
            word in tournamentPlayerSearchText("${p.name} ${p.fullName.orEmpty()} ${data.teamByPlayer[p.id].orEmpty()}")
        }
    }.sortedWith(compareBy<PlayerDto> { it.id !in data.teamByPlayer }.thenBy { it.fullName ?: it.name })
    val matches = matchingPlayers.filter { it.id !in selected }

    Div(attrs = { classes("tournament-slot", "tournament-slot-detail") }) {
        Div(attrs = { classes("slot-detail-heading") }) {
            Div {
                H4 { Text(slotTimeLabel(slot)) }
                slot.label?.takeIf { it.isNotBlank() }?.let { P { Text(it) } }
            }
            Button(attrs = { if (busy) disabled(); onClick { onClose() } }) { Text("Zavřít detail") }
        }
        P(attrs = { classes("slot-detail-capacity"); if (selected.size > slot.capacity) classes("over") }) {
            Text("Obsazeno ${selected.size} z ${slot.capacity}")
            if (selected.size > slot.capacity) Text(" — kapacita překročena")
        }
        if (dirty) P(attrs = { classes("slot-pending") }) { Text("Neuložené změny obsazení") }
        if (selected.isEmpty()) P { Text("Zatím prázdný slot.") }
        else Table(attrs = { classes("public-table") }) {
            Tr { Th { Text("Hráč") }; Th { Text("Tým / host") }; if (canEdit) Th { } }
            selected.sortedBy { playerById[it]?.fullName ?: playerById[it]?.name ?: it }.forEach { id ->
                val player = playerById[id]
                Tr {
                    Td {
                        Text(player?.fullName ?: player?.name ?: baseline.firstOrNull { it.playerId == id }?.playerName ?: id)
                        if (id !in baselineIds) Span(attrs = { classes("tag") }) { Text("nově vybrán") }
                        guests.firstOrNull { it.id == id }?.let { guest ->
                            Span(attrs = { classes("tag") }) { Text("host · skupina ${guest.groupCode}") }
                        }
                        if (id in moves) Span(attrs = { classes("tag") }) { Text("přesun po uložení") }
                    }
                    Td { Text(teamOrGuestLabel(id, data.teamByPlayer)) }
                    if (canEdit) Td { Button(attrs = {
                        if (busy) disabled()
                        onClick { selected = selected - id; moves = moves - id; guests = guests.filter { it.id != id }; saved = false }
                    }) { Text("Odebrat") } }
                }
            }
        }
        if (canEdit) {
            Div(attrs = { classes("slot-player-search") }) {
                Label { Text("Přidat hráče: ") }
                Input(type = InputType.Text) {
                    classes("wide-input"); attr("aria-label", "Hledat hráče do slotu")
                    placeholder("Jméno nebo tým — lze psát bez diakritiky")
                    value(search); if (busy) disabled(); onInput { search = it.value }
                }
                if (words.isNotEmpty()) {
                    if (matches.isEmpty()) {
                        if (matchingPlayers.isNotEmpty()) P { Text("Nalezení hráči už jsou vybraní v tomto slotu.") }
                        else {
                            P { Text("Žádný hráč neodpovídá hledání. Můžete ho založit jako hosta.") }
                            Div(attrs = { classes("tournament-slot", "slot-new-guest") }) {
                                H4 { Text("Nový host") }
                                Label { Text("Jméno hosta: ") }
                                Input(type = InputType.Text) {
                                    value(guestName); attr("aria-label", "Jméno hosta"); if (busy) disabled()
                                    onInput { guestName = it.value }
                                }
                                Label { Text("Pohlaví: ") }
                                Select(attrs = {
                                    attr("aria-label", "Pohlaví hosta"); if (busy) disabled()
                                    onChange { guestGender = it.value ?: "male" }
                                }) {
                                    Option("male", attrs = { if (guestGender == "male") selected() }) { Text("Muž") }
                                    Option("female", attrs = { if (guestGender == "female") selected() }) { Text("Žena") }
                                }
                                Label { Text("Skupina: ") }
                                Select(attrs = {
                                    attr("aria-label", "Skupina hosta"); if (busy) disabled()
                                    onChange { guestGroup = it.value ?: "B" }
                                }) {
                                    listOf("A", "B").forEach { group ->
                                        Option(group, attrs = { if (guestGroup == group) selected() }) { Text(group) }
                                    }
                                }
                                Button(attrs = {
                                    if (busy || guestName.isBlank() || guestName.trim().length > 100) disabled()
                                    onClick {
                                        if (allPlayers.any { tournamentPlayerSearchText(it.fullName ?: it.name) == tournamentPlayerSearchText(guestName.trim()) }) {
                                            error = "Hráč s tímto jménem už je v evidenci nebo ve výběru. Vyhledejte ho podle jména."
                                            return@onClick
                                        }
                                        val id = window.asDynamic().crypto.randomUUID() as String
                                        guests = guests + TournamentSlotGuest(id, guestName.trim(), guestGender, guestGroup)
                                        selected = selected + id; saved = false
                                    }
                                }) { Text("Přidat hosta do obsazení") }
                                P(attrs = { classes("muted") }) { Text("Host, přihláška i zvolená skupina se založí tlačítkem Uložit obsazení.") }
                            }
                        }
                    }
                    matches.take(12).forEach { player ->
                        val sourceSlotId = entryByPlayer[player.id]?.slotId?.takeIf { it != slot.id }
                        val sourceSlot = sourceSlotId?.let { slotById[it] }
                        Div(attrs = { classes("slot-player-result") }) {
                            Span {
                                Text("${player.fullName ?: player.name} — ${teamOrGuestLabel(player.id, data.teamByPlayer)}")
                                if (sourceSlot != null) {
                                    Text(" · v jiném slotu: ${formatDate(sourceSlot.startsAt.take(10))} ${slotTimeLabel(sourceSlot)}")
                                    if (sourceSlot.dayLabel.isNotBlank()) Text(" · ${sourceSlot.dayLabel}")
                                    sourceSlot.label?.let { Text(" · $it") }
                                }
                            }
                            Button(attrs = {
                                if (busy) disabled()
                                attr("aria-label", "${if (sourceSlotId == null) "Přidat" else "Přesunout"} ${player.fullName ?: player.name}")
                                onClick {
                                    if (sourceSlotId != null) {
                                        moveToConfirm = TournamentSlotMove(player.id, sourceSlotId)
                                    } else {
                                        selected = selected + player.id; saved = false
                                    }
                                }
                            }) { Text(if (sourceSlotId == null) "Přidat" else "Přesunout…") }
                        }
                    }
                    if (matches.size > 12) P(attrs = { classes("muted") }) { Text("Zobrazeno 12 z ${matches.size} hráčů. Zpřesněte hledání.") }
                }
            }
            moveToConfirm?.let { move ->
                val player = playerById[move.playerId]
                val source = slotById[move.fromSlotId]
                Div(attrs = { classes("tournament-slot"); attr("role", "alertdialog"); attr("aria-label", "Potvrzení přesunu hráče") }) {
                    H4 { Text("Potvrdit přesun hráče") }
                    P { Text(player?.fullName ?: player?.name ?: move.playerId) }
                    P { Text("Z: " + (source?.let { "${formatDate(it.startsAt.take(10))} ${slotTimeLabel(it)} · ${it.dayLabel} · ${it.label.orEmpty()}" } ?: "původní slot")) }
                    P { Text("Do: ${formatDate(slot.startsAt.take(10))} ${slotTimeLabel(slot)} · ${slot.dayLabel} · ${slot.label.orEmpty()}") }
                    P { Text("Přesun se provede po uložení obsazení. Přihláška, skupina a výsledky hráče zůstanou zachované.") }
                    Div(attrs = { classes("actions") }) {
                        Button(attrs = {
                            if (busy) disabled()
                            onClick {
                                moves = moves + (move.playerId to move.fromSlotId)
                                selected = selected + move.playerId; saved = false; moveToConfirm = null
                            }
                        }) { Text("Potvrdit přesun") }
                        Button(attrs = { if (busy) disabled(); onClick { moveToConfirm = null } }) { Text("Zrušit přesun") }
                    }
                }
            }
            Div(attrs = { classes("actions") }) {
                Button(attrs = {
                    if (busy || !dirty || moveToConfirm != null) disabled()
                    onClick {
                        busy = true; error = null; saved = false
                        val additions = (selected - baselineIds).filter { id -> guests.none { it.id == id } }
                        val removals = baseline.filter { it.playerId !in selected }.map { it.id }
                        scope.launch {
                            try {
                                Api.saveTournamentSlotPlayers(token, slot.id, additions, removals,
                                    moves.filterKeys { it in selected }.map { TournamentSlotMove(it.key, it.value) },
                                    guests.filter { it.id in selected })
                                val refreshed = Api.fetchTournamentEntries(token, slot.tournamentId).filter { it.slotId == slot.id }
                                baseline = refreshed; selected = refreshed.map { it.playerId }.toSet()
                                guests = emptyList(); moves = emptyMap()
                                saved = true
                                onReload()
                            } catch (e: Throwable) { error = "Uložení obsazení selhalo: ${e.apiMessage()}" }
                            busy = false
                        }
                    }
                }) { Text(if (busy) "Ukládám…" else "Uložit obsazení") }
                if (dirty) Button(attrs = {
                    if (busy) disabled(); onClick { selected = baselineIds; guests = emptyList(); moves = emptyMap(); saved = false }
                }) { Text("Zrušit změny obsazení") }
            }
            P(attrs = { classes("muted") }) {
                Text("Vyberte více hráčů a pak uložte obsazení. Noví hráči se současně přihlásí do turnaje. " +
                    "Odebrání ze slotu zachová přihlášku i výsledky. Přesun z jiného slotu je nutné potvrdit.")
            }
            if (saved) P(attrs = { classes("slot-save-status"); attr("role", "status") }) { Text("Obsazení uloženo.") }
            error?.let { P(attrs = { classes("editor-error") }) { Text(it) } }
            Div(attrs = { classes("actions") }) {
                Button(attrs = { if (busy || dirty) disabled(); onClick { editing = !editing } }) { Text(if (editing) "Zavřít úpravu slotu" else "Upravit slot") }
                Button(attrs = {
                    if (busy || dirty) disabled()
                    onClick {
                        if (window.confirm("Smazat slot? Přihlášky a výsledky hráčů zůstanou zachované.")) {
                            busy = true
                            scope.launch {
                                try { Api.deleteTournamentSlot(token, slot.id); onReload() }
                                catch (e: Throwable) { error = "Smazání slotu selhalo: ${e.apiMessage()}" }
                                busy = false
                            }
                        }
                    }
                }) { Text("Smazat slot") }
            }
            if (editing) TournamentSlotForm(
                token, slot.tournamentId, slot.day(), slot.sortOrder, slot,
                onCancel = { editing = false }, onSaved = { editing = false; onReload() },
            )
        }
    }
}
